import { $t } from '@/i18n';
import Vue from 'vue';
import { debounce, throttle } from 'lodash';
import { defineComponent, onMounted, onUnmounted, computed } from '@vue/composition-api';
import cookies from 'js-cookie';
import { BASE_PATH } from '@/config/constant';
import { getReloginURL } from '@utils/userInfo';
import wujiFetch from '@/utils/wujiFetch';
import { BASE_API_PATH } from '@config/constant';
import { isOffline } from '@/utils/offline';
import { isWechat } from '@/utils/screen';
// 使用 Promise 锁代替布尔变量，确保同一时间仅一个 reLogin 操作执行
let pendingPromise = null;
export const reLogin = async () => {
    // 如果已经有一个 reLogin 操作正在执行，直接返回那个 Promise
    if (pendingPromise) {
        return pendingPromise;
    }
    // 创建 Promise 锁，并保存 resolve 函数用于后续解锁
    let resolvePending;
    pendingPromise = new Promise((resolve) => {
        resolvePending = resolve;
    });
    const cleanup = () => {
        pendingPromise = null;
        resolvePending();
    };
    // 先进行一次检测，如果登录态正常就不弹窗
    const isLoggedIn = await detect();
    if (isLoggedIn) {
        cleanup();
        return;
    }
    let warningMsg = '';
    const ssoReason = cookies.get('xy-sso-logout-reason');
    switch (ssoReason) {
        case 'session is expired':
            warningMsg = $t('k_5b26cc4d');
            break;
        case 'session is terminated':
            warningMsg = $t('k_d5f24c87');
            break;
        case 'security policy updated':
            warningMsg = $t('k_82f993d9');
            break;
        default:
            warningMsg = ssoReason || '';
    }
    const instance = new Vue({
        methods: {
            onClose() {
                cleanup();
                this.$destroy();
            },
        },
        render() {
            return (<ReLoginModal warningMsg={warningMsg} onClose={this.onClose}/>);
        },
    });
    const el = document.createElement('div');
    document.body.appendChild(el);
    instance.$mount(el);
};
// 请求接口判断是否有登录态
const detect = async () => {
    const initialUser = window.GLOBAL_INFO?.rtx;
    if (!initialUser || initialUser === 'guest' || isOffline())
        return true;
    try {
        // 注意：wujiFetch 自己会调用 reLogin 显示弹窗
        const result = await wujiFetch(`${BASE_API_PATH}/xy/healthcheck`);
        return !!result;
    }
    catch (err) {
        return false; // server down
    }
};
const detectIfNeeded = debounce(() => {
    if (document.visibilityState !== 'visible')
        return;
    if (pendingPromise)
        return; // 如果有正在进行的 reLogin 操作，跳过检测
    detect();
}, 3000, { leading: true, trailing: false });
// when page got focus or visible, detect once.
setTimeout(() => {
    window.addEventListener('focus', detectIfNeeded, false);
    document.addEventListener('visibilitychange', detectIfNeeded, false);
}, 1000);
const MAX_COUNT = 150;
const ReLoginModal = defineComponent({
    name: 'ReLoginModal',
    props: {
        warningMsg: String,
    },
    setup(props, { emit }) {
        const handleClose = () => emit('close');
        const warnMsg = computed(() => props.warningMsg || $t('k_44e3bc79'));
        let count = 0;
        const detectAndClose = throttle(() => {
            if (document.visibilityState !== 'visible')
                return;
            detect().then((result) => {
                if (result) {
                    handleClose();
                    detectAndClose.cancel();
                }
                else {
                    count += 1;
                    if (count < MAX_COUNT)
                        setTimeout(detectAndClose, 1000);
                }
            });
        }, 1000, { leading: true, trailing: true });
        const handleReLogin = () => {
            if (isWechat()) {
                window.location.reload();
            }
            else {
                // 跳转到新窗口，重新拉起登录...登录后新页面立刻关闭
                const loginPageUrl = `${window.location.origin}${BASE_PATH}login`;
                window.open(getReloginURL(loginPageUrl));
            }
        };
        const handleLoginSuccess = (e) => {
            if (e?.data?.event === 'login')
                handleClose();
        };
        onMounted(() => {
            detectAndClose();
            window.addEventListener('focus', detectAndClose, false);
            window.addEventListener('message', handleLoginSuccess);
        });
        onUnmounted(() => {
            window.removeEventListener('focus', detectAndClose, false);
            window.removeEventListener('message', handleLoginSuccess);
        });
        return {
            handleReLogin,
            warnMsg,
        };
    },
    render() {
        return (<a-modal class="[&_.ant-modal-body]:px-8 [&_.ant-modal-body]:pt-8 [&_.ant-modal-body]:pb-6" visible={true} centered={true} footer={null} closable={false}>
        <div class="flex">
          <a-icon class="text-[22px] text-[#faad14] mr-4" type="exclamation-circle"/>
          <div>
            <div class="text-[rgba(0,0,0,.85)] font-medium text-base leading-[22px]">{$t('k_26a3fe00')}</div>
            <div class="mt-2">{this.warnMsg}</div>
          </div>
        </div>
        <div class="flex justify-center mt-6">
          <a-space>
            <a-button type="primary" onClick={this.handleReLogin}>{$t('k_737f422d')}</a-button>
          </a-space>
        </div>
      </a-modal>);
    },
});
